import React, { useEffect, useState } from "react";
import axios from "axios";
import { ArrowRight, Clock } from "lucide-react";
import Reveal from "./Reveal";
import { POSTS } from "../mock/mock";
import { Button } from "./ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Blog() {
  const [posts, setPosts] = useState(POSTS);
  const [loading, setLoading] = useState(true);
  const [live, setLive] = useState(false);

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/blog/posts`, { timeout: 25000 })
      .then((res) => {
        if (active && Array.isArray(res.data) && res.data.length > 0) {
          setPosts(res.data.slice(0, 3));
          setLive(true);
        }
      })
      .catch(() => {
        /* keep mock fallback */
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  return (
    <section id="blog" className="bg-[#f5fbfc] dark:bg-slate-950 py-24 sm:py-32 transition-colors">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <p className="text-cyan-600 dark:text-cyan-400 font-bold tracking-widest uppercase text-sm">Blog</p>
                {live && (
                  <span className="flex items-center gap-1.5 text-xs font-semibold text-teal-600 dark:text-teal-400 bg-teal-50 dark:bg-teal-500/10 px-2.5 py-1 rounded-full">
                    <span className="w-2 h-2 rounded-full bg-teal-500 animate-pulse" /> Live feed
                  </span>
                )}
              </div>
              <h2 className="font-display font-800 text-4xl sm:text-5xl lg:text-6xl text-slate-900 dark:text-white leading-tight">
                Inside the depths.
              </h2>
              <p className="mt-4 text-lg text-slate-600 dark:text-slate-400 max-w-xl">
                The latest news and updates on our games, our journey and indie development.
              </p>
            </div>
            <a href="https://blog.sharkbowlgames.com/" target="_blank" rel="noopener noreferrer">
              <Button
                variant="outline"
                className="rounded-full border-teal-600 text-teal-700 dark:text-cyan-300 dark:border-cyan-400/40 hover:bg-teal-50 dark:hover:bg-white/5 px-6"
              >
                View all posts <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-8 mt-16">
          {loading
            ? [0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="rounded-3xl overflow-hidden bg-white dark:bg-slate-900 border border-cyan-100 dark:border-white/10 animate-pulse"
                >
                  <div className="aspect-[16/10] bg-slate-200 dark:bg-slate-800" />
                  <div className="p-6 space-y-3">
                    <div className="h-4 w-24 bg-slate-200 dark:bg-slate-800 rounded" />
                    <div className="h-5 w-full bg-slate-200 dark:bg-slate-800 rounded" />
                    <div className="h-4 w-3/4 bg-slate-200 dark:bg-slate-800 rounded" />
                  </div>
                </div>
              ))
            : posts.map((post, i) => (
                <Reveal key={post.id} delay={i * 100}>
                  <a
                    href={post.link || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tilt-card group h-full flex flex-col rounded-[1.75rem] overflow-hidden bg-white dark:bg-slate-900 border-2 border-cyan-100 dark:border-white/10 shadow-[0_10px_40px_rgba(14,127,168,0.06)]"
                  >
                    <div className="aspect-[16/10] overflow-hidden bg-slate-100 dark:bg-slate-800">
                      {post.img ? (
                        <img
                          src={post.img}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                      ) : (
                        <div className="w-full h-full ocean-gradient" />
                      )}
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-xs font-bold uppercase tracking-wider text-teal-600 dark:text-teal-400 bg-teal-50 dark:bg-teal-500/10 px-2.5 py-1 rounded-full">
                          {post.category}
                        </span>
                        <span className="flex items-center gap-1 text-slate-400 text-xs">
                          <Clock className="w-3.5 h-3.5" /> {post.read}
                        </span>
                      </div>
                      <h3 className="font-display font-700 text-xl text-slate-900 dark:text-white leading-snug group-hover:text-teal-700 dark:group-hover:text-cyan-300 transition-colors">
                        {post.title}
                      </h3>
                      <p className="text-slate-500 dark:text-slate-400 mt-3 flex-1">{post.excerpt}</p>
                      <div className="flex items-center justify-between mt-5 pt-4 border-t border-slate-100 dark:border-white/5">
                        <span className="text-sm text-slate-400">{post.date}</span>
                        <span className="text-teal-600 dark:text-cyan-400 font-semibold text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                          Read <ArrowRight className="w-4 h-4" />
                        </span>
                      </div>
                    </div>
                  </a>
                </Reveal>
              ))}
        </div>
      </div>
    </section>
  );
}
